import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MapPreviewPanel = ({ 
  cities = [], 
  selectedCity = null, 
  onCitySelect, 
  isCollapsed = false, 
  onToggleCollapse 
}) => {
  const [mapView, setMapView] = useState('overview'); // overview, detailed
  const [hoveredCity, setHoveredCity] = useState(null);

  const handleCityClick = (city) => {
    onCitySelect(city);
  };

  const getMapDimensions = () => {
    return { width: 1000, height: 600 };
  };

  const renderCityMarker = (city) => {
    const isSelected = selectedCity?.id === city?.id;
    const isHovered = hoveredCity?.id === city?.id;
    const scale = isSelected || isHovered ? 1.2 : 1;
    
    return (
      <g
        key={city?.id}
        transform={`translate(${city?.svgX}, ${city?.svgY}) scale(${scale})`}
        className="cursor-pointer transition-transform duration-200"
        onClick={() => handleCityClick(city)}
        onMouseEnter={() => setHoveredCity(city)}
        onMouseLeave={() => setHoveredCity(null)}
      >
        {/* City Marker */}
        <circle
          cx="0"
          cy="0"
          r="8"
          fill="#2563eb"
          stroke="white"
          strokeWidth="2"
          className="drop-shadow-sm"
        />
        {/* Sub-union count indicator */}
        {city?.subUnionCount > 0 && (
          <circle
            cx="12"
            cy="-12"
            r="6"
            fill="#059669"
            stroke="white"
            strokeWidth="1"
          />
        )}
        {/* City name label */}
        {(isSelected || isHovered) && (
          <g>
            <rect
              x="-30"
              y="15"
              width="60"
              height="20"
              rx="4"
              fill="rgba(0,0,0,0.8)"
            />
            <text
              x="0"
              y="28"
              textAnchor="middle"
              fill="white"
              fontSize="10"
              fontFamily="Arial, sans-serif"
            >
              {city?.nameAr}
            </text>
          </g>
        )}
      </g>
    );
  };

  const renderMapBackground = () => {
    const { width, height } = getMapDimensions();
    
    return (
      <g>
        {/* Background */}
        <rect width={width} height={height} fill="#f8fafc" />
        
        {/* Grid lines */}
        <defs>
          <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
            <path d="M 50 0 L 0 0 0 50" fill="none" stroke="#e2e8f0" strokeWidth="1" opacity="0.3"/>
          </pattern>
        </defs>
        <rect width={width} height={height} fill="url(#grid)" />
        
        {/* Border */}
        <rect 
          width={width} 
          height={height} 
          fill="none" 
          stroke="#cbd5e1" 
          strokeWidth="2"
        />
        
        {/* Regions (mock geographical areas) */}
        <g opacity="0.1">
          <ellipse cx="200" cy="150" rx="80" ry="60" fill="#3b82f6" />
          <ellipse cx="500" cy="200" rx="120" ry="80" fill="#10b981" />
          <ellipse cx="750" cy="300" rx="100" ry="70" fill="#f59e0b" />
          <ellipse cx="300" cy="400" rx="90" ry="65" fill="#8b5cf6" />
          <ellipse cx="650" cy="450" rx="110" ry="75" fill="#ef4444" />
        </g>
      </g>
    );
  };

  if (isCollapsed) {
    return (
      <div className="lg:hidden">
        <Button
          variant="outline"
          size="sm"
          onClick={onToggleCollapse}
          iconName="Map"
          iconPosition="left"
          className="w-full"
        >
          عرض الخريطة ({cities?.length} مدينة)
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm">
      {/* Panel Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="Map" size={20} className="text-primary" />
          <div>
            <h3 className="text-sm font-semibold text-foreground">خريطة المدن</h3>
            <p className="text-xs text-muted-foreground">
              {cities?.length} مدينة • {cities?.filter(c => c?.subUnionCount > 0)?.length} نشطة
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant={mapView === 'overview' ? 'default' : 'ghost'}
            size="xs"
            onClick={() => setMapView('overview')}
          >
            عام
          </Button>
          
          <Button
            variant={mapView === 'detailed' ? 'default' : 'ghost'}
            size="xs"
            onClick={() => setMapView('detailed')}
          >
            تفصيلي
          </Button>
          
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={onToggleCollapse}
            iconName="X"
            iconSize={16}
          />
        </div>
      </div>
      {/* Map Container */}
      <div className="p-4">
        <div className="relative bg-background border border-border rounded-lg overflow-hidden">
          <svg
            viewBox={`0 0 ${getMapDimensions()?.width} ${getMapDimensions()?.height}`}
            className="w-full h-64 lg:h-80"
            style={{ maxHeight: '400px' }}
          >
            {renderMapBackground()}
            
            {/* Cities */}
            {cities?.map(renderCityMarker)}
          </svg>
          
          {/* Map Controls */}
          <div className="absolute top-2 right-2 flex flex-col space-y-1">
            <Button
              variant="secondary"
              size="icon"
              className="w-8 h-8 shadow-sm"
              iconName="ZoomIn"
              iconSize={14}
              title="تكبير"
            />
            <Button
              variant="secondary"
              size="icon"
              className="w-8 h-8 shadow-sm"
              iconName="ZoomOut"
              iconSize={14}
              title="تصغير"
            />
            <Button
              variant="secondary"
              size="icon"
              className="w-8 h-8 shadow-sm"
              iconName="RotateCcw"
              iconSize={14}
              title="إعادة تعيين"
            />
          </div>
          
          {/* Legend */}
          <div className="absolute bottom-2 left-2 bg-background/90 border border-border rounded p-2 text-xs">
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-primary" />
                <span>مدن نشطة</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-muted-foreground" />
                <span>مدن غير نشطة</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-success" />
                <span>يحتوي على اتحادات فرعية</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Selected City Info */}
      {selectedCity && (
        <div className="p-4 border-t border-border bg-muted/30">
          <div className="flex items-start space-x-3">
            <div className="flex items-center justify-center w-6 h-6 rounded border border-border bg-muted text-xs font-medium text-foreground mt-1">
              {selectedCity?.colorZone || 0}
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-foreground">{selectedCity?.nameAr}</h4>
              {selectedCity?.nameEn && (
                <p className="text-sm text-muted-foreground">{selectedCity?.nameEn}</p>
              )}
              <div className="flex items-center space-x-4 mt-2 text-xs text-muted-foreground">
                <span>الإحداثيات: ({selectedCity?.svgX}, {selectedCity?.svgY})</span>
                <span>الاتحادات الفرعية: {selectedCity?.subUnionCount}</span>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Statistics */}
      <div className="p-4 border-t border-border">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-lg font-semibold text-foreground">{cities?.length}</div>
            <div className="text-xs text-muted-foreground">إجمالي المدن</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-success">
              {cities?.filter(c => c?.subUnionCount > 0)?.length}
            </div>
            <div className="text-xs text-muted-foreground">مدن نشطة</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-primary">
              {cities?.reduce((sum, city) => sum + city?.subUnionCount, 0)}
            </div>
            <div className="text-xs text-muted-foreground">اتحادات فرعية</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapPreviewPanel;